import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { apiClient } from "../../../../api/httpClient";
import { formatAsCurrency, scrollToTop } from "../formHelpers";
import {
  calculateAgeFromDate,
  hydrateBasicInfoFromStorage,
  hydrateBasicInfoOnMount,
  recalcBasicInfoPremium,
} from "../basicInfoPremiumHelpers";

/**
 * Shared basic-information hook for creation + historic surfaces.
 */
export function useBondBasicInformationCore({ data, updateData, config, surface = "creation" }) {
  const [state, setState] = useState(null);
  const [type, setType] = useState(null);
  const [counties, setCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);

  const {
    premiumStrategy,
    defaultCountField,
    minorAgeValidation,
    probateCountyConditionals,
    navigate,
    logContext,
    showCounty,
    esignatureMode,
    esignatureThreshold = 50001,
  } = config;

  const recalcPremium = (updatedData, withDefaultCount = true) =>
    recalcBasicInfoPremium(
      updatedData,
      { premiumStrategy, defaultCountField, esignatureMode, esignatureThreshold },
      withDefaultCount
    );

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (
      minorAgeValidation &&
      name === "dateOfDeath" &&
      data.bondSubCategory === "Guardian - Minor"
    ) {
      const age = calculateAgeFromDate(value);
      if (age >= 18) {
        toast.error("Ward must be under 18 years old for Guardian - Minor bonds");
      }
    }

    const premiumTriggerFields =
      premiumStrategy === "guardianship"
        ? ["bondAmount", "dateOfDeath", "bondSubCategory"]
        : ["bondAmount"];

    if (premiumTriggerFields.includes(name)) {
      const updatedData = { ...data, [name]: value };
      updateData(updatedData);
      const withPremium = recalcPremium(updatedData);
      if (withPremium) updateData(withPremium);
    } else {
      updateData({ ...data, [name]: value });
    }
  };

  const handleAutocompleteChange = (event, value, fieldName) => {
    const updatedData = { ...data, [fieldName]: value };
    updateData(updatedData);

    if (
      premiumStrategy === "guardianship" &&
      fieldName === "bondSubCategory" &&
      updatedData.bondAmount &&
      updatedData.dateOfDeath
    ) {
      const withPremium = recalcPremium(updatedData, false);
      if (withPremium) updateData(withPremium);
    }
  };

  const handleCourtCaseToggle = (e) => {
    const val = e.target.value;
    if (val === "No") {
      updateData({ ...data, hasCourtCaseNumber: "No", courtCaseNumber: "" });
    } else {
      updateData({ ...data, hasCourtCaseNumber: val });
    }
  };

  useEffect(() => {
    hydrateBasicInfoOnMount({
      surface,
      config,
      data,
      updateData,
      state,
      setState,
      type,
      setType,
    });
    scrollToTop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (surface === "historic") return;
    hydrateBasicInfoFromStorage("selectedState", data?.state, state, setState, logContext);
    hydrateBasicInfoFromStorage("selectedBondType", data?.bondType, type, setType, logContext);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.state, data?.bondType, surface]);

  useEffect(() => {
    if (!showCounty) return undefined;

    const fetchCounties = async () => {
      if (state?.id) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(`/counties-by-state/${state.id}/`, "GET");
          setCounties(countyResponse.map((county) => county.bond_county));
        } catch (error) {
          console.error("Error fetching counties:", error);
        } finally {
          setLoadingCounties(false);
        }
      }
    };

    fetchCounties();
  }, [state, showCounty]);

  useEffect(() => {
    if (!probateCountyConditionals) return undefined;
    if (data.county === "Pasco" && data.judgeStern !== "Yes") {
      updateData({ ...data, judgeStern: "Yes" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.county, probateCountyConditionals]);

  useEffect(() => {
    if (!probateCountyConditionals || data.judgeStern !== "No" || !navigate) return undefined;
    const timer = setTimeout(() => navigate(-1), 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.judgeStern, probateCountyConditionals, navigate]);

  return {
    counties,
    loadingCounties,
    formatAsCurrency,
    handleChange,
    handleAutocompleteChange,
    handleCourtCaseToggle,
  };
}
